<template>
  <div class="py-t flex flex-col overflow-hidden h-full">
    <div v-if="isLoading" class="flex justify-center items-center h-full">
      <div class="loading loading-ring loading-lg"></div>
    </div>
    <template v-else>
      <!-- 编辑、预览、排序按钮 -->
      <div
        v-if="timelineEvents.length > 0 && enableEdit"
        class="flex gap-2 items-center m-2 relative"
      >
        <button
          v-for="modeItem in modeList"
          :key="modeItem"
          :class="[
            'btn btn-xs',
            mode === modeItem ? 'btn-primary btn-outline' : '',
          ]"
          @click="clickModeChange(modeItem)"
        >
          {{ $t(`Buttons.${modeItem}`) }}
        </button>

        <div
          class="absolute left-[20px] top-[100%] bg-[--van-popover-light-background] rounded-lg"
          v-if="mode === 'sort'"
        >
          <div class="text-xs p-2">
            {{ $t(`Message.dragModuleToSort`) }}
          </div>
        </div>
      </div>

      <!-- 最后更新、时间顺序、时间倒序 -->
      <div class="flex justify-between items-center" v-if="mode === 'preview'">
        <boxed-tab
          :selected-idx="activeSortIdx"
          class="m-1"
          @update:selected-idx="onSorterChange"
        >
          <div v-for="(item, idx) in SORTER_ENUM" :key="idx">
            {{ item.text }}
          </div>
        </boxed-tab>

        <!-- 编辑按钮 -->
        <div
          v-if="
            !enableEdit &&
            moduleStore.isMyEntity(props.entityId, props.entityType)
          "
        >
          <button
            class="btn btn-xs btn-outline mr-2"
            @click="clickToModuleEdit"
          >
            {{ $t(`Buttons.edit`) }}
          </button>
        </div>
      </div>

      <TimelineModuleList
        @update:is-saving="emit('update:isSaving', $event)"
        :timeline-events="timelineEvents"
        :mode="mode"
        @reload="init"
        :event-loading="eventLoading"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { readTimelineEventPage } from "@/apis/user";
import { useModule } from "@/stores/module";
import { TIMELINE_MODULE_ID as MATE_TIMELINE_MODULE_ID } from "@/stores/module/moduleMate";
import { TIMELINE_MODULE_ID as WORLDVIEW_TIMELINE_MODULE_ID } from "@/stores/module/moduleWorldview";
import { ModuleEntityType, TimelineEvent } from "@/types/entity";
import { onActivated, onMounted, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const props = defineProps({
  enableEdit: {
    type: Boolean,
    default: true,
  },
  entityId: {
    type: Number,
    required: true,
  },
  entityUuid: {
    type: String,
    required: true,
  },
  entityType: {
    type: String as PropType<ModuleEntityType>,
    required: true,
  },
  universeId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits<{
  (e: "update:isSaving", value: boolean): void;
}>();

watch(
  () => [props.entityId, props.entityType],
  () => {
    init();
  }
);

const timelineEvents = ref<TimelineEvent[]>([]);
const moduleStore = useModule();
const router = useRouter();
type Mode = "edit" | "preview" | "sort";

const modeList: Mode[] = ["edit", "sort", "preview"];
const mode = ref<Mode>(props.enableEdit ? "edit" : "preview");

const { t } = useI18n();

const activeSortIdx = ref(0);

const SORTER_ENUM: { text: string; value: string }[] = [
  { text: t("module.timeline.updateDesc"), value: "dtUpdate-desc" },
  { text: t("module.timeline.timeAsc"), value: "order-asc" },
  { text: t("module.timeline.timeDesc"), value: "order-desc" },
];

const onSorterChange = async (idx: number) => {
  activeSortIdx.value = idx;
  await init();
};

const eventLoading = ref(false);
const init = async () => {
  if (mode.value !== "preview") {
    activeSortIdx.value = 1;
  }

  const sort = SORTER_ENUM[activeSortIdx.value].value;
  const [field, order] = sort.split("-");
  eventLoading.value = true;
  let params: {
    entityId?: number;
    entityType: ModuleEntityType;
    universeId: number;
  } = {
    entityId: props.entityId,
    entityType: props.entityType,
    universeId: props.universeId,
  };

  if (props.entityId != params.entityId) return;
  if (props.entityId === 0) {
    delete params.entityId;
  }

  const res = await readTimelineEventPage(params, {}, { field, order });
  timelineEvents.value = res.page.data;
  eventLoading.value = false;
};

const clickToModuleEdit = () => {
  if (props.entityType === "worldview") {
    router.push({
      name: "worldviewEdit",
      params: {
        uuid: props.entityUuid,
      },
      hash: "#" + WORLDVIEW_TIMELINE_MODULE_ID,
    });
  } else {
    router.push({
      name: "mateEdit",
      params: { uuid: props.entityUuid },
      hash: "#" + MATE_TIMELINE_MODULE_ID,
    });
  }
};

const clickModeChange = (modeItem: Mode) => {
  mode.value = modeItem;
  if (modeItem === "edit" || modeItem === "sort") {
    onSorterChange(1);
  }
};

const isLoading = ref(false);

onActivated(async () => {
  isLoading.value = true;
  await init();
  isLoading.value = false;
});

onMounted(async () => {
  isLoading.value = true;
  await init();
  isLoading.value = false;
});
</script>
